import React, { useState, useEffect } from 'react';
import Modal from '../../components/modal/modal';
import Input from '../../components/input/Input';
import DropDown from '../../components/dropdown/DropDown';
import Card from '../../components/card/Card';
import { getCommissionTypes } from '../../services/GuaranteesService';

interface NewCommissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { commission_type_id: number; name: string; amount: number }) => Promise<void>;
}

interface CommissionType {
  id: number;
  name: string;
}

const NewCommissionModal: React.FC<NewCommissionModalProps> = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [commissionTypes, setCommissionTypes] = useState<CommissionType[]>([]);
  const [selectedCommissionType, setSelectedCommissionType] = useState<CommissionType | null>(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCommissionTypes();
    }
  }, [isOpen]);

  const fetchCommissionTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await getCommissionTypes();
      if (response.success && 'data' in response) {
        const types = Array.isArray(response.data) ? response.data : response.data.content || [];
        setCommissionTypes(types);
      }
    } catch (error) {
      console.error('Error fetching commission types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !selectedCommissionType || !amount.trim()) {
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue)) {
      return;
    }

    setLoading(true);
    try {
      await onSave({
        commission_type_id: selectedCommissionType.id,
        name: name.trim(),
        amount: amountValue,
      });
      handleClose();
    } catch (error) {
      console.error('Error saving commission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setSelectedCommissionType(null);
    setAmount('');
    onClose();
  };

  const handleCommissionTypeChange = (selectedLabel: string) => {
    const type = commissionTypes.find((t) => t.name === selectedLabel);
    if (type) {
      setSelectedCommissionType(type);
    }
  };

  const isFormValid = name.trim() !== '' && selectedCommissionType !== null && amount.trim() !== '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nueva comisión"
      primaryText="Guardar"
      primaryVariant="primary"
      onPrimaryAction={handleSave}
      primaryActionDisabled={!isFormValid}
      loadingPrimaryBtn={loading}
      secondaryText="Cancelar"
      secondaryVariant="contained"
      onSecondaryAction={handleClose}
      width="500px"
    >
      <Card margin={{ top: '4' }} gap='16px'>
        <Card>
          <Input
            id="commission-name"
            label="Nombre"
            type="text"
            size="md"
            placeholder="Ingrese el nombre de la comisión"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Card>

        <Card margin={{ top: '3' }}>
          <DropDown
            id="commission-type"
            label="Forma de comisionar"
            size="md"
            title={selectedCommissionType?.name || 'Seleccione una opción'}
            items={commissionTypes.map((type) => ({ label: type.name }))}
            onChange={handleCommissionTypeChange}
            disabled={loadingTypes}
          />
        </Card>

        <Card margin={{ top: '2' }}>
          <Input
            id="commission-amount"
            label="Porcentaje de comisión"
            type="number"
            size="md"
            placeholder="Ingrese el porcentaje"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Card>
      </Card>
    </Modal>
  );
};

export default NewCommissionModal;
